import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { t } from "@lingui/macro";
import { Trans } from "@lingui/react/macro";

const TEMPLATES = [
  {
    desktop: "/template-narrative-desktop.webp",
    mobile: "/template-narrative-mobile.webp",
  },
  {
    desktop: "/template-editorial-desktop.webp",
    mobile: "/template-editorial-mobile.webp",
  },
  {
    desktop: "/template-whimsical-desktop.webp",
    mobile: "/template-whimsical-mobile.webp",
  },
  {
    desktop: "/template-traditional-desktop.webp",
    mobile: "/template-traditional-mobile.webp",
  },
  {
    desktop: "/template-watercolor-desktop.webp",
    mobile: "/template-watercolor-mobile.webp",
  },
];

const INTERVAL = 4000;
const FADE_DURATION = 1000;

let currentIndex = 0;
let timerId: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function ensureTimer() {
  if (timerId !== null || typeof window === "undefined") {
    return;
  }
  timerId = setInterval(() => {
    currentIndex = (currentIndex + 1) % TEMPLATES.length;
    for (const listener of listeners) {
      listener();
    }
  }, INTERVAL);
}

function subscribe(callback: () => void) {
  ensureTimer();
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
    if (listeners.size === 0 && timerId !== null) {
      clearInterval(timerId);
      timerId = null;
    }
  };
}

function useTemplateIndex() {
  return useSyncExternalStore(
    subscribe,
    () => currentIndex,
    () => 0,
  );
}

function CrossfadeImage({ src }: { src: string }) {
  const [slots, setSlots] = useState<[string, string]>([src, src]);
  const [activeSlot, setActiveSlot] = useState<0 | 1>(0);
  const lastSrc = useRef(src);

  useEffect(() => {
    if (src === lastSrc.current) {
      return;
    }
    lastSrc.current = src;
    let cancelled = false;

    const applySwap = () => {
      if (cancelled) {
        return;
      }
      setSlots((prev) => {
        const next: [string, string] = [prev[0], prev[1]];
        next[activeSlot === 0 ? 1 : 0] = src;
        return next;
      });
      setActiveSlot((prev) => (prev === 0 ? 1 : 0));
    };

    const img = new Image();
    img.src = src;
    if (typeof img.decode === "function") {
      img.decode().then(applySwap, applySwap);
    } else if (img.complete) {
      applySwap();
    } else {
      img.addEventListener("load", applySwap, { once: true });
      img.addEventListener("error", applySwap, { once: true });
    }

    return () => {
      cancelled = true;
    };
  }, [src, activeSlot]);

  return (
    <>
      {slots.map((slotSrc, i) => (
        <img
          alt=""
          aria-hidden={i !== activeSlot}
          className="absolute inset-0 h-full w-full object-cover object-top"
          decoding="async"
          draggable={false}
          fetchPriority={i === 0 ? "high" : "low"}
          key={i}
          src={slotSrc}
          style={{
            opacity: i === activeSlot ? 1 : 0,
            transition: `opacity ${FADE_DURATION}ms ease`,
            willChange: "opacity",
          }}
        />
      ))}
    </>
  );
}

export function PhoneMockup({
  widthClass = "w-[100px] sm:w-[160px] lg:w-[200px]",
}: {
  widthClass?: string;
}) {
  const index = useTemplateIndex();

  return (
    <div className={`relative shrink-0 ${widthClass}`}>
      <div className="overflow-hidden rounded-[1.5rem] border-[3px] border-[#1a1a1a] bg-white">
        {/* Notch */}
        <div className="relative z-10 mx-auto h-[6px] w-[40%] rounded-b-lg bg-[#1a1a1a]" />
        <div className="relative aspect-[9/19.5] overflow-hidden">
          <CrossfadeImage src={TEMPLATES[index].mobile} />
        </div>
      </div>
    </div>
  );
}

export function DesktopMockup({
  widthClass = "w-[220px] sm:w-[400px] lg:w-[540px]",
}: {
  widthClass?: string;
}) {
  const index = useTemplateIndex();

  return (
    <div className={`relative shrink-0 ${widthClass}`}>
      {/* Monitor */}
      <div className="overflow-hidden rounded-t-[0.25rem] border-[2px] border-[#1a1a1a]">
        {/* Screen */}
        <div className="relative aspect-[16/10] overflow-hidden bg-white">
          <CrossfadeImage src={TEMPLATES[index].desktop} />
        </div>
        {/* Chin */}
        <div className="h-[14px] bg-[#1a1a1a] sm:h-[18px]">
          <div className="mx-auto mt-[4px] h-[4px] w-[30px] rounded-full bg-[#333] sm:mt-[5px] sm:h-[5px] sm:w-[40px]" />
        </div>
      </div>
      {/* Stand */}
      <div className="mx-auto h-[20px] w-[60px] bg-[#1a1a1a] sm:h-[28px] sm:w-[70px]" />
      <div className="mx-auto h-[4px] w-[100px] rounded-b-sm bg-[#1a1a1a] sm:w-[120px]" />
    </div>
  );
}

type CarouselTab = "mobile" | "desktop";

const SWIPE_COMMIT_RATIO = 0.25;

export function MobilePreviewCarousel() {
  const [active, setActive] = useState<CarouselTab>("mobile");
  const [dragPercent, setDragPercent] = useState<number | null>(null);
  const startX = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    setDragPercent(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const w = containerRef.current?.clientWidth ?? 0;
    if (w === 0) {
      return;
    }
    const dxPercent = ((e.touches[0].clientX - startX.current) / w) * 100;
    const clamped =
      active === "mobile"
        ? Math.max(-100, Math.min(0, dxPercent))
        : Math.max(0, Math.min(100, dxPercent));
    setDragPercent(clamped);
  };

  const handleTouchEnd = () => {
    if (
      dragPercent !== null &&
      Math.abs(dragPercent) > SWIPE_COMMIT_RATIO * 100
    ) {
      setActive(active === "mobile" ? "desktop" : "mobile");
    }
    setDragPercent(null);
  };

  const effectiveDrag = dragPercent ?? 0;
  const carouselPercent = (active === "mobile" ? 0 : -100) + effectiveDrag;
  const togglePercent = -carouselPercent;
  const isDragging = dragPercent !== null;
  const transitionClass = isDragging
    ? ""
    : "transition-transform duration-300 ease-out";

  return (
    <div className="flex flex-col items-center">
      <div
        className="w-full overflow-hidden"
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
        ref={containerRef}
        style={{ touchAction: "pan-y" }}
      >
        <div
          className={`flex ${transitionClass}`}
          style={{ transform: `translateX(${carouselPercent}%)` }}
        >
          <div
            aria-hidden={active !== "mobile"}
            className="flex w-full shrink-0 justify-center"
            id="device-preview-mobile"
            role="tabpanel"
          >
            <PhoneMockup widthClass="w-[200px]" />
          </div>
          <div
            aria-hidden={active !== "desktop"}
            className="flex w-full shrink-0 items-center justify-center"
            id="device-preview-desktop"
            role="tabpanel"
          >
            <DesktopMockup widthClass="w-[300px]" />
          </div>
        </div>
      </div>
      <div
        aria-label={t`Tipo de vista previa`}
        className="relative mt-6 inline-grid grid-cols-2 rounded-full border border-[var(--color-accent)] bg-white p-[3px]"
        role="tablist"
      >
        <span
          aria-hidden="true"
          className={`absolute left-[3px] top-[3px] h-[calc(100%-6px)] w-[calc(50%-3px)] rounded-full bg-[var(--color-accent)] ${transitionClass}`}
          style={{ transform: `translateX(${togglePercent}%)` }}
        />
        <button
          aria-controls="device-preview-mobile"
          aria-selected={active === "mobile"}
          className={`relative z-10 cursor-pointer rounded-full px-5 py-1.5 text-sm font-medium transition-colors ${
            active === "mobile" ? "text-white" : "text-[#1a1a1a]"
          }`}
          onClick={() => setActive("mobile")}
          role="tab"
          type="button"
        >
          <Trans>Móvil</Trans>
        </button>
        <button
          aria-controls="device-preview-desktop"
          aria-selected={active === "desktop"}
          className={`relative z-10 cursor-pointer rounded-full px-5 py-1.5 text-sm font-medium transition-colors ${
            active === "desktop" ? "text-white" : "text-[#1a1a1a]"
          }`}
          onClick={() => setActive("desktop")}
          role="tab"
          type="button"
        >
          <Trans>Escritorio</Trans>
        </button>
      </div>
    </div>
  );
}
